import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import { WorkflowStepper } from '../ai-workflow/components/WorkflowStepper';
import { GeneratedResults } from '../ai-workflow/components/GeneratedResults';
import { downloadImage } from './aiDesignerApi';
import { GenderSetting, getClothType } from './config/clothTypes';
import { getClothIcon } from './clothIcons';
import { AIDesignerInitialState, useAIDesigner } from './hooks/useAIDesigner';
import UploadCard from './components/UploadCard';
import ExampleStrip from './components/ExampleStrip';
import CategoryStep from './components/CategoryStep';
import GarmentTypeStep from './components/GarmentTypeStep';
import AttributesStep from './components/AttributesStep';
import DesignerSummaryReview from './components/DesignerSummaryReview';
import DesignerSummarySidebar from './components/DesignerSummarySidebar';

interface LocationState extends AIDesignerInitialState {
  recreatingFrom?: string;
}

type Step = 'UPLOAD' | 'CATEGORY' | 'GARMENT' | 'ATTRIBUTES' | 'SUMMARY';

const STEPS: { key: Step; label: string }[] = [
  { key: 'UPLOAD', label: 'Upload' },
  { key: 'CATEGORY', label: 'Category' },
  { key: 'GARMENT', label: 'Garment' },
  { key: 'ATTRIBUTES', label: 'Attributes' },
  { key: 'SUMMARY', label: 'Summary & Generate' },
];

interface StepFooterProps {
  onBack?: () => void;
  onNext: () => void;
  nextLabel: string;
}

const StepFooter: React.FC<StepFooterProps> = ({ onBack, onNext, nextLabel }) => (
  <div className="mt-8 flex items-center justify-between">
    {onBack ? (
      <button
        type="button"
        onClick={onBack}
        className="rounded-xl border border-gray-300 px-6 py-3 font-bold text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50"
      >
        Back
      </button>
    ) : (
      <span />
    )}
    <button
      type="button"
      onClick={onNext}
      className="group flex items-center space-x-2 rounded-xl bg-secondary-600 px-8 py-3.5 font-bold text-white shadow-lg transition-all duration-300 hover:bg-secondary-700 hover:shadow-xl"
    >
      <span>{nextLabel}</span>
      <i className="fas fa-arrow-right transition-transform group-hover:translate-x-1" />
    </button>
  </div>
);

const AIDesigner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialState = useMemo(() => (location.state as LocationState) || {}, [location.state]);

  const {
    clothImage,
    hasPrimaryImage,
    uploading,
    uploadError,
    addImage,
    removeImage,
    loadExample,
    clearAll,
    settings,
    updateSettings,
    attributes,
    setAttributes,
    mode,
    setMode,
    modeRestrictions,
    creditEstimation,
    userCredits,
    generatedImages,
    isExample,
    isProcessing,
    generationTime,
    error,
    generate,
    examplePresets,
  } = useAIDesigner(initialState);

  const [currentStep, setCurrentStep] = useState<Step>(
    initialState.generatedImages?.length ? 'SUMMARY' : 'UPLOAD'
  );
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  const selectedClothType =
    settings.clothType && settings.clothType !== 'auto'
      ? getClothType(settings.clothType, settings.gender)
      : undefined;

  const handleGenderChange = (gender: GenderSetting) => {
    updateSettings({ gender, clothType: 'auto' });
    setAttributes({});
  };

  const handleClothTypeChange = (clothType: string) => {
    updateSettings({ clothType });
    setAttributes({});
  };

  const handleClearAttributes = () => setAttributes({});

  const handleRemoveAttribute = (label: string, value: string) => {
    const next = { ...attributes };
    next[label] = (next[label] || []).filter((item) => item !== value);
    if (next[label].length === 0) delete next[label];
    setAttributes(next);
  };

  const handleGenerate = async () => {
    const outcome = await generate();
    if (outcome === 'insufficient-credits') {
      setShowCreditsModal(true);
    } else if (outcome === 'not-authenticated') {
      alert('Please log in to generate content');
    }
  };

  const handleStartNew = () => {
    clearAll();
    setCurrentStep('UPLOAD');
  };

  const nextStep = () => {
    const index = STEPS.findIndex((step) => step.key === currentStep);
    if (currentStep === 'UPLOAD' && !hasPrimaryImage) {
      alert('Please upload the primary cloth image.');
      return;
    }
    if (currentStep === 'GARMENT' && !selectedClothType) {
      alert('Please choose a garment type.');
      return;
    }
    if (index < STEPS.length - 1) setCurrentStep(STEPS[index + 1].key);
  };

  const prevStep = () => {
    const index = STEPS.findIndex((step) => step.key === currentStep);
    if (index <= 0) {
      navigate('/');
      return;
    }
    setCurrentStep(STEPS[index - 1].key);
  };

  const goToStep = (step: Step) => {
    const targetIndex = STEPS.findIndex((item) => item.key === step);
    const currentIndex = STEPS.findIndex((item) => item.key === currentStep);
    if (targetIndex >= 0 && targetIndex < currentIndex) {
      setCurrentStep(step);
    }
  };

  const isSummary = currentStep === 'SUMMARY';
  const hasResults = generatedImages.length > 0;
  const mainColClass = !isSummary || hasResults ? 'lg:col-span-4' : 'lg:col-span-3';

  return (
    <div className="min-h-screen bg-white">
      <WorkflowStepper
        steps={STEPS}
        currentStep={currentStep}
        onStepClick={goToStep}
        onPrev={prevStep}
        onNext={isSummary ? undefined : nextStep}
      />

      <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className={mainColClass}>
            {/* Step 1: Upload */}
            {currentStep === 'UPLOAD' && (
              <div className="mx-auto w-full max-w-6xl py-2">
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-gray-900">Upload your garment</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Use a clear, well-lit photo of the cloth for the best results.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
                  <div className="mx-auto w-full max-w-lg lg:mx-0 lg:max-w-none">
                    <UploadCard
                      title="Drag & drop your garment"
                      hint="or click to browse · front view required"
                      image={clothImage}
                      isExample={isExample}
                      uploading={uploading}
                      disabled={isProcessing}
                      onSelect={(file) => addImage(file)}
                      onRemove={removeImage}
                    />
                    {uploadError && (
                      <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-center text-xs font-medium text-red-600">
                        {uploadError}
                      </p>
                    )}
                  </div>

                  <div>
                    <ExampleStrip
                      presets={examplePresets}
                      onSelect={loadExample}
                      disabled={isProcessing}
                    />
                  </div>
                </div>

                <StepFooter onNext={nextStep} nextLabel="Continue to Category" />
              </div>
            )}

            {/* Step 2: Category */}
            {currentStep === 'CATEGORY' && (
              <div className="mx-auto w-full max-w-6xl py-2">
                <CategoryStep gender={settings.gender} onSelect={handleGenderChange} />
                <StepFooter onBack={prevStep} onNext={nextStep} nextLabel="Continue to Garment" />
              </div>
            )}

            {/* Step 3: Garment type */}
            {currentStep === 'GARMENT' && (
              <div className="mx-auto w-full max-w-6xl py-2">
                <GarmentTypeStep
                  gender={settings.gender}
                  clothType={settings.clothType}
                  onSelect={handleClothTypeChange}
                />
                <StepFooter onBack={prevStep} onNext={nextStep} nextLabel="Continue to Attributes" />
              </div>
            )}

            {/* Step 4: Attributes */}
            {currentStep === 'ATTRIBUTES' && (
              <div className="mx-auto w-full max-w-6xl py-2">
                <AttributesStep
                  gender={settings.gender}
                  clothType={settings.clothType}
                  attributes={attributes}
                  onAttributesChange={setAttributes}
                  onClear={handleClearAttributes}
                />
                <StepFooter onBack={prevStep} onNext={nextStep} nextLabel="Proceed to Summary" />
              </div>
            )}

            {/* Step 5: Summary & generate */}
            {isSummary && (
              <div className="mx-auto w-full max-w-6xl py-2">
                {hasResults ? (
                  <div className="space-y-4">
                    {isExample && (
                      <p className="rounded-xl border border-secondary-100 bg-secondary-50 px-4 py-2.5 text-xs font-medium text-secondary-700">
                        These are sample results. Upload your own garment and generate to create your own designs.
                      </p>
                    )}
                    <GeneratedResults
                      images={generatedImages}
                      onStartNew={handleStartNew}
                      onDownload={(url, index) => downloadImage(url, `ai-designer-result-${index + 1}.jpg`)}
                      onNavigateToCanvas={(url, tool) =>
                        navigate('/canvas-editor', { state: { inputImages: [url], tool } })
                      }
                    />
                  </div>
                ) : isProcessing ? (
                  <div
                    className="flex flex-col items-center justify-center rounded-2xl border border-gray-200/80 bg-gray-50/50 p-8 text-center"
                    style={{ minHeight: 'calc(100vh - 220px)' }}
                  >
                    <div className="mx-auto max-w-sm space-y-6">
                      <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
                        <div className="absolute inset-0 animate-pulse rounded-full border-4 border-secondary-100" />
                        <div className="absolute inset-0 animate-spin rounded-full border-4 border-t-secondary-600" />
                        <span className="z-10 font-mono text-xs font-bold text-secondary-700">
                          {generationTime.toFixed(1)}s
                        </span>
                      </div>
                      <div>
                        <h4 className="mb-1 text-lg font-bold text-gray-800">Designing your garment</h4>
                        <p className="animate-pulse text-xs font-medium text-secondary-600">
                          Synthesizing the selected design direction...
                        </p>
                      </div>
                      <p className="text-xs text-gray-400">This may take up to a minute.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <DesignerSummaryReview
                      clothImage={clothImage}
                      gender={settings.gender}
                      garment={selectedClothType}
                      attributes={attributes}
                      onRemoveAttribute={handleRemoveAttribute}
                    />
                    {error && (
                      <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                        <i className="fas fa-exclamation-triangle text-red-500" />
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary sidebar */}
          {isSummary && !hasResults && (
            <div className="animate-slideDown lg:col-span-1">
              <DesignerSummarySidebar
                inputImageUrls={clothImage ? [clothImage] : []}
                garmentName={selectedClothType?.name}
                garmentIcon={selectedClothType ? getClothIcon(selectedClothType.id) : undefined}
                attributes={attributes}
                mode={mode}
                availableModes={modeRestrictions.availableModes}
                creditEstimation={creditEstimation}
                onModeChange={setMode}
                onGenerate={handleGenerate}
                isProcessing={isProcessing}
                generationTime={generationTime}
              />
            </div>
          )}
        </div>
      </div>

      <InsufficientCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="AI Designer Generation"
      />
    </div>
  );
};

export default AIDesigner;
